import React, { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Users, Truck, Wrench, Package, ShoppingCart, Receipt, Warning, Coins } from "@phosphor-icons/react";
import MonthlySummary from "@/components/MonthlySummary";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Cell } from "recharts";

const KPI = ({ icon: Icon, label, value, sub, testid, tone = "primary" }) => (
  <Card className="kpi-card p-5 border border-border" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
        <p className="font-display text-3xl font-bold mt-2 tabular-nums">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </div>
      <div className={`p-2 rounded ${tone === "accent" ? "bg-[hsl(24.6_95%_53.1%)]/10 text-[hsl(24.6_95%_53.1%)]" : "bg-primary/10 text-primary"}`}>
        <Icon size={22} weight="duotone" />
      </div>
    </div>
  </Card>
);

const CHART_COLORS = ["hsl(222.2 47.4% 11.2%)", "hsl(24.6 95% 53.1%)", "hsl(142.1 76.2% 36.3%)", "hsl(199 89% 48%)", "hsl(38 92% 50%)"];

export default function Dashboard() {
  const [k, setK] = useState(null);
  useEffect(() => { api.get("/dashboard/kpis").then(r => setK(r.data)); }, []);

  if (!k) return <div className="p-8 text-muted-foreground">Loading dashboard...</div>;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="font-display text-3xl font-bold">Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Business overview at a glance.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={Coins} label="Stock Value" value={money(k.total_stock_value)} sub={`${k.total_stock_qty} units on hand`} testid="kpi-stock-value" tone="accent" />
        <KPI icon={Receipt} label="Total Sales" value={money(k.total_sales)} sub={`${k.total_customers} customers`} testid="kpi-total-sales" />
        <KPI icon={ShoppingCart} label="Total Purchase" value={money(k.total_purchase)} sub={`${k.total_vendors} vendors`} testid="kpi-total-purchase" />
        <KPI icon={Warning} label="Low Stock Items" value={k.low_stock_count} sub="Below reorder level" testid="kpi-low-stock" tone="accent" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KPI icon={Users} label="Customers" value={k.total_customers} testid="kpi-customers" />
        <KPI icon={Truck} label="Vendors" value={k.total_vendors} testid="kpi-vendors" />
        <KPI icon={Wrench} label="Technicians" value={k.total_technicians} testid="kpi-technicians" />
        <KPI icon={Package} label="Items / Parts" value={k.total_items} testid="kpi-items" />
      </div>

      <MonthlySummary />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-6 border border-border">
          <h3 className="font-display font-semibold mb-4">Stock Value by Brand</h3>
          {k.brands?.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={k.brands} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(214.3 31.8% 91.4%)" />
                <XAxis dataKey="brand" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => money(v)} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {k.brands.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[260px] flex items-center justify-center text-sm text-muted-foreground">No stock data yet</div>
          )}
        </Card>
        <Card className="p-6 border border-border">
          <h3 className="font-display font-semibold mb-4">Recent Stock Movements</h3>
          <div className="space-y-2">
            {(k.recent_movements || []).slice(0, 8).map((m, i) => (
              <div key={i} className="flex items-center justify-between text-sm border-b border-border pb-2 last:border-0">
                <div>
                  <span className="inline-block px-2 py-0.5 text-[10px] font-semibold rounded uppercase tracking-wider mr-2 bg-secondary">
                    {m.movement_type}
                  </span>
                  <span className="text-muted-foreground">Qty {m.qty}</span>
                </div>
                <div className="text-xs text-muted-foreground">{new Date(m.created_at).toLocaleDateString("en-IN")}</div>
              </div>
            ))}
            {!k.recent_movements?.length && <p className="text-sm text-muted-foreground">No movements yet</p>}
          </div>
        </Card>
      </div>
    </div>
  );
}
